<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const canAddItems = computed(() => store.getters.hasPermission('inventory.add'));
const canEditItems = computed(() => store.getters.hasPermission('inventory.edit'));
const canDeleteItems = computed(() => store.getters.hasPermission('inventory.delete'));
const canExportStock = computed(() => store.getters.hasPermission('reports.stock.export'));
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader
          :title="title"
          :title1="title1"
          :items="canAddItems ? items : []"
        />

        <!-- Summary Pills -->
        <div class="stock-summary-bar mb-3">
          <div class="summary-pill all" @click="selectedStockStatus = 'all'" :class="{ active: selectedStockStatus === 'all' }">
            <i class="fas fa-boxes me-1"></i>
            <span>All Items</span>
            <strong>{{ stockSummary.totalitems }}</strong>
          </div>
          <div class="summary-pill warning" @click="selectedStockStatus = 'low_stock'" :class="{ active: selectedStockStatus === 'low_stock' }">
            <i class="fas fa-exclamation-triangle me-1"></i>
            <span>Low Stock</span>
            <strong>{{ stockSummary.lowstock }}</strong>
          </div>
          <div class="summary-pill danger" @click="selectedStockStatus = 'out_of_stock'" :class="{ active: selectedStockStatus === 'out_of_stock' }">
            <i class="fas fa-times-circle me-1"></i>
            <span>Out of Stock</span>
            <strong>{{ stockSummary.nostock }}</strong>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div class="search-set">
                <div class="search-input">
                  <a class="btn btn-searchset">
                    <img src="../../../assets/img/icons/search-white.svg" alt="img" />
                  </a>
                  <div class="dataTables_filter">
                    <label>
                      <input
                        type="search"
                        v-model="search"
                        class="form-control form-control-sm"
                        placeholder="Search by name, code, HSN..."
                        @input="searching"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div class="table-top-right">
                <div class="top-filter-select">
                  <model-list-select
                    :list="categoryListFromApi"
                    v-model="selectedCategory"
                    option-value="categoryid"
                    option-text="categoryname"
                    placeholder="All Categories"
                  />
                </div>

                <a
                  v-if="canExportStock"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Export CSV"
                  @click="exportToCSV"
                  class="export-btn"
                >
                  <img src="../../../assets/img/icons/excel.svg" alt="Export" class="excel-icon" />
                </a>
              </div>
            </div>

            <div class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="filteredItems"
                :loading="tableLoading"
                :pagination="paginationConfig"
                :expanded-row-keys="expandedRowKeys"
                row-key="itemid"
                @change="handleTableChange"
                @expand="handleExpand"
              >
                <template #bodyCell="{ column, record }">

                  <template v-if="column.key === 'itemname'">
                    <div class="item-name-cell">
                      <a
                        @click="viewProduct(record)"
                        href="javascript:void(0);"
                        class="item-name-link"
                      >{{ record.itemname }}</a>
                      <span class="item-code-tag" v-if="record.itemcode">{{ record.itemcode }}</span>
                    </div>
                  </template>

                  <template v-else-if="column.key === 'unitprice'">
                    <span class="price-value">₹{{ formatNum(record.unitprice) }}</span>
                  </template>

                  <template v-else-if="column.key === 'available'">
                    <div class="stock-cell">
                      <span class="stock-val" :class="getStockClass(record)">
                        {{ formatNum(record.available) }}
                      </span>
                      <span class="stock-unit">{{ record.unit }}</span>
                      <span class="stock-dot" :class="getStockClass(record)"></span>
                    </div>
                  </template>

                  <template v-else-if="column.key === 'hsncode'">
                    <span class="mono-text">{{ record.hsncode || '—' }}</span>
                  </template>

                  <template v-else-if="column.key === 'taxpercent'">
                    <span v-if="record.taxpercent" class="gst-badge">{{ record.taxpercent }}%</span>
                    <span v-else class="text-muted">—</span>
                  </template>

                  <template v-else-if="column.key === 'minstock'">
                    <span class="threshold-val">{{ record.minstock ?? record.threshold ?? '—' }}</span>
                  </template>

                  <template v-else-if="column.key === 'action'">
                    <div class="action-cell">
                      <a
                        href="javascript:void(0);"
                        @click="viewProduct(record)"
                        class="action-btn view"
                        title="View Details"
                      >
                        <img src="../../../assets/img/icons/eye.svg" alt="view" />
                      </a>

                      <template v-if="canEditItems || canDeleteItems">
                        <a
                          v-if="canEditItems"
                          href="javascript:void(0);"
                          @click="editProduct(record)"
                          class="action-btn edit me-2"
                          title="Edit"
                        >
                          <img src="../../../assets/img/icons/edit.svg" alt="edit" />
                        </a>
                        <a
                          v-if="canDeleteItems"
                          href="javascript:void(0);"
                          @click="showDeleteAlert(record.itemid)"
                          class="action-btn delete"
                          title="Delete"
                        >
                          <img src="../../../assets/img/icons/delete.svg" alt="delete" />
                        </a>
                      </template>
                    </div>
                  </template>

                </template>

                <template #expandedRowRender="{ record }">
                  <div class="expanded-wrap">
                    <div v-if="detailLoading[record.itemid]" class="expanded-loading">Loading purchase history...</div>
                    <div v-else-if="itemPurchaseDetails[record.itemid] && itemPurchaseDetails[record.itemid].items.length">
                      <div class="detail-header">
                        <span>Recent Purchases for <strong>{{ record.itemname }}</strong></span>
                        <span>
                          Showing <strong>{{ itemPurchaseDetails[record.itemid].items.length }}</strong>
                          of <strong>{{ itemPurchaseDetails[record.itemid].total }}</strong>
                        </span>
                      </div>
                      <table class="detail-table">
                        <thead>
                          <tr>
                            <th>Bill No</th>
                            <th>Supplier</th>
                            <th>Invoice No</th>
                            <th>Received Date</th>
                            <th>Invoice Date</th>
                            <th>Qty</th>
                            <th>Free</th>
                            <th>Remaining</th>
                            <th>Purchase Price</th>
                            <th>Net PTR</th>
                            <th>MRP</th>
                            <th>Batch</th>
                            <th>Expiry</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in itemPurchaseDetails[record.itemid].items" :key="row.purchaseitemid">
                            <td>
                              <button class="bill-link" @click="viewPurchase(row.purchaseid)">
                                {{ row.billno || row.purchaseid }}
                              </button>
                            </td>
                            <td>{{ row.vendor || '-' }}</td>
                            <td>{{ row.invoiceno || '-' }}</td>
                            <td>{{ formatDate(row.purchasedate) }}</td>
                            <td>{{ formatDate(row.invoicedate) }}</td>
                            <td>{{ formatNum(row.quantity) }}</td>
                            <td>{{ formatNum(row.freequantity || 0) }}</td>
                            <td>
                              <span :class="remainingQtyClass(row)">{{ formatNum(row.remainingqty) }}</span>
                            </td>
                            <td>₹{{ formatNum(row.purchaseprice) }}</td>
                            <td>₹{{ formatNum(row.netpurchaserate ?? row.purchaseprice) }}</td>
                            <td>₹{{ formatNum(row.mrp) }}</td>
                            <td>{{ row.batchno || '-' }}</td>
                            <td>{{ formatDate(row.expirydate) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else class="expanded-loading">No purchase records found for this item.</div>
                  </div>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { ModelListSelect } from "vue-search-select";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const columns = [
  {
    title: "Item Name",
    dataIndex: "itemname",
    key: "itemname",
    sorter: {
      compare: (a, b) =>
        a.itemname.toLowerCase() < b.itemname.toLowerCase() ? -1 : 1,
    },
  },
  {
    title: "Category",
    dataIndex: "itemcategory",
    key: "itemcategory",
    sorter: {
      compare: (a, b) =>
        (a.itemcategory || "").toLowerCase() < (b.itemcategory || "").toLowerCase() ? -1 : 1,
    },
  },
  {
    title: "Unit",
    dataIndex: "unit",
    key: "unit",
    sorter: {
      compare: (a, b) =>
        (a.unit || "").toLowerCase() < (b.unit || "").toLowerCase() ? -1 : 1,
    },
  },
  {
    title: "Rack No",
    dataIndex: "rackno",
    key: "rackno",
    sorter: {
      compare: (a, b) =>
        (a.rackno || "").toLowerCase() < (b.rackno || "").toLowerCase() ? -1 : 1,
    },
  },
  {
    title: "Selling Price",
    dataIndex: "unitprice",
    key: "unitprice",
    sorter: { compare: (a, b) => a.unitprice - b.unitprice },
  },
  {
    title: "Available",
    dataIndex: "available",
    key: "available",
    sorter: { compare: (a, b) => a.available - b.available },
  },
  {
    title: "Min Stock",
    dataIndex: "minstock",
    key: "minstock",
    sorter: { compare: (a, b) => ((a.minstock ?? a.threshold) || 0) - ((b.minstock ?? b.threshold) || 0) },
  },
  {
    title: "HSN Code",
    dataIndex: "hsncode",
    key: "hsncode",
    sorter: {
      compare: (a, b) =>
        (a.hsncode || "").toLowerCase() < (b.hsncode || "").toLowerCase() ? -1 : 1,
    },
  },
  {
    title: "GST %",
    dataIndex: "taxpercent",
    key: "taxpercent",
    sorter: { compare: (a, b) => (a.taxpercent || 0) - (b.taxpercent || 0) },
  },
  {
    title: "Action",
    key: "action",
  },
];

export default {
  name: "productlist",
  components: { ModelListSelect },

  data() {
    return {
      title: "Item List",
      title1: "Manage your items",
      items: [{ text: "Add New Item", addname: "/addproduct" }],
      data: null,
      tableLoading: false,
      search: "",
      columns,
      selectedStockStatus: "all",
      selectedCategory: "",
      categoryListFromApi: [{ categoryid: "", categoryname: "All Categories" }],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
      searchTimer: null,
      stockSummary: {
        totalitems: 0,
        lowstock: 0,
        nostock: 0,
      },
      expandedRowKeys: [],
      itemPurchaseDetails: {},
      detailLoading: {},
    };
  },

  computed: {
    paginationConfig() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      };
    },

    selectedCategoryName() {
      if (!this.selectedCategory) return null;
      const found = this.categoryListFromApi.find(
        (c) => String(c.categoryid) === String(this.selectedCategory)
      );
      return found && found.categoryname !== "All Categories"
        ? found.categoryname
        : null;
    },

    lowStockList() {
      if (!this.data) return [];
      return this.data.filter(
        (item) =>
          parseFloat(item.available) > 0 &&
          parseFloat(item.available) <= parseFloat((item.minstock ?? item.threshold) || 0)
      );
    },

    outOfStockList() {
      if (!this.data) return [];
      return this.data.filter((item) => parseFloat(item.available) <= 0);
    },

    filteredItems() {
      return this.data || [];
    },
  },

  watch: {
    selectedStockStatus() {
      this.pagination.current = 1;
      this.getItems();
    },
    selectedCategory() {
      this.pagination.current = 1;
      this.getItems();
    },
  },

  methods: {
    async getItems() {
      this.tableLoading = true;
      try {
        const response = await axios.get("/v1/items", {
          params: {
            page: this.pagination.current,
            page_size: this.pagination.pageSize,
            search: this.search.trim() || undefined,
            stock_status: this.selectedStockStatus === "all" ? undefined : this.selectedStockStatus,
            category: this.selectedCategoryName || undefined,
            stocked_only: true,
          },
        });
        this.data = response.data.items || [];
        this.pagination = {
          current: response.data.page || this.pagination.current,
          pageSize: response.data.page_size || this.pagination.pageSize,
          total: response.data.total || 0,
        };
      } catch (error) {
        console.error("Error fetching items:", error);
        Swal.fire({
          title: "Failed to load items",
          text: error.response?.data?.detail || error.message,
          icon: "error",
        });
      } finally {
        this.tableLoading = false;
      }
    },

    async getStockSummary() {
      try {
        const response = await axios.get("/v1/items/stock-summary");
        this.stockSummary = {
          totalitems: response.data.totalitems || 0,
          lowstock: response.data.lowstock || 0,
          nostock: response.data.nostock || 0,
        };
        if (!this.pagination.total && response.data.totalitems && this.selectedStockStatus === "all") {
          this.pagination.total = response.data.totalitems;
        }
      } catch (error) {
        console.error("Error fetching item summary:", error);
      }
    },

    async fetchCategories() {
      try {
        const response = await axios.get("/v1/category");
        this.categoryListFromApi = [
          { categoryid: "", categoryname: "All Categories" },
          ...response.data,
        ];
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },

    viewProduct(record) {
      this.$router.push({ path: "/product-details", query: { id: record.itemid } });
    },

    viewPurchase(purchaseid) {
      this.$router.push({ path: "/purchase-details", query: { id: purchaseid } });
    },

    handleExpand(expanded, record) {
      if (expanded) {
        this.expandedRowKeys = [...new Set([...this.expandedRowKeys, record.itemid])];
        this.fetchItemPurchases(record.itemid);
      } else {
        this.expandedRowKeys = this.expandedRowKeys.filter((id) => id !== record.itemid);
      }
    },

    async fetchItemPurchases(itemid) {
      if (this.itemPurchaseDetails[itemid] || this.detailLoading[itemid]) return;
      this.detailLoading = { ...this.detailLoading, [itemid]: true };
      try {
        const response = await axios.get(`/v1/items/${itemid}/purchases`, {
          params: { limit: 10 },
        });
        this.itemPurchaseDetails = {
          ...this.itemPurchaseDetails,
          [itemid]: response.data || { items: [], total: 0 },
        };
      } catch (error) {
        console.error("Error fetching item purchases:", error);
        Swal.fire({
          title: "Failed to load purchases",
          text: error.response?.data?.detail || error.message,
          icon: "error",
        });
      } finally {
        this.detailLoading = { ...this.detailLoading, [itemid]: false };
      }
    },

    formatDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },

    remainingQtyClass(row) {
      const remaining = parseFloat(row.remainingqty) || 0;
      if (remaining <= 0) return "remaining-out";
      if (remaining < parseFloat(row.quantity || 0)) return "remaining-partial";
      return "remaining-full";
    },

    editProduct(record) {
      this.$router.push({ path: "/editproduct", query: { id: record.itemid } });
    },

    showDeleteAlert(id) {
      Swal.fire({
        title: "Are you sure?",
        text: "This item will be permanently deleted.",
        showCancelButton: true,
        confirmButtonColor: "#FF9F43",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (!result.isConfirmed) return;
        axios
          .delete("/v1/items/" + id)
          .then(() => {
            Swal.fire({
              title: "Deleted!",
              text: "Item has been removed.",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            const index = this.data.findIndex((o) => o.itemid === id);
            if (index !== -1) this.data.splice(index, 1);
            this.pagination.total = Math.max(0, this.pagination.total - 1);
            this.getStockSummary();
          })
          .catch((error) => {
            console.error(error);
            Swal.fire(error.response?.data?.detail || "Delete failed.");
          });
      });
    },

    exportToCSV() {
      const items = this.filteredItems;
      const headers = [
        "Item ID", "Item Name", "Description", "Category",
        "Item Code", "Barcode", "Rack No", "Unit",
        "Purchase Rate", "MRP", "Selling Price", "Wholesale Rate",
        "Available", "Min Stock",
        "HSN Code", "Tax %", "CGST %", "SGST %",
        "UOM Upper", "UOM Lower", "Conversion Factor",
      ];

      const escape = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val);
        return str.includes(",") || str.includes('"') || str.includes("\n")
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      };

      const csvContent = [
        headers.join(","),
        ...items.map((item) =>
          [
            item.itemid, item.itemname, item.itemdescription, item.itemcategory,
            item.itemcode, item.barcode, item.rackno, item.unit,
            item.purchaserate, item.mrp, item.unitprice, item.wholesalerate,
            item.available, item.minstock ?? item.threshold,
            item.hsncode, item.taxpercent, item.cgst, item.sgst,
            item.uom_upper, item.uom_lower, item.conversionfactor,
          ].map(escape).join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `items_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
    },

    formatNum(val) {
      if (val === null || val === undefined || val === "") return "0";
      return parseFloat(val).toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    getStockClass(record) {
      const avail = parseFloat(record.available) || 0;
      const threshold = parseFloat(record.minstock ?? record.threshold) || 0;
      if (avail <= 0) return "stock-out";
      if (avail <= threshold) return "stock-low";
      return "stock-ok";
    },

    searching() {
      this.pagination.current = 1;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.getItems();
      }, 300);
    },

    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.getItems();
    },

    handleKeydown(event) {
      if (
        this.$store.getters.hasPermission("inventory.add") &&
        this.$route.path === "/productlist" &&
        event.ctrlKey &&
        (event.key === "m" || event.key === "M")
      ) {
        event.preventDefault();
        this.$router.push("/addproduct");
      }
    },
  },

  beforeMount() {
    const stockFilter = this.$route.query.stock || this.$route.query.category;
    if (["low_stock", "out_of_stock"].includes(stockFilter)) {
      this.selectedStockStatus = stockFilter;
    }
    this.getItems();
    this.getStockSummary();
    this.fetchCategories();
  },

  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
    document.removeEventListener("keydown", this.handleKeydown);
  },
};
</script>

<style scoped>
/* =============================================
   ✅ PAGINATION FIX — clipped "10 / page" dropdown
   ============================================= */
:deep(.ant-pagination) {
  display: flex !important;
  align-items: center !important;
  flex-wrap: wrap !important;
  padding: 12px 0 4px 0 !important;
  overflow: visible !important;
}

:deep(.ant-pagination-options) {
  display: inline-flex !important;
  align-items: center !important;
  height: auto !important;
  overflow: visible !important;
  margin-left: 8px !important;
}

:deep(.ant-pagination-options .ant-select) {
  height: 32px !important;
  line-height: 32px !important;
  overflow: visible !important;
}

:deep(.ant-pagination-options .ant-select-selector) {
  height: 32px !important;
  padding: 0 24px 0 10px !important;
  display: flex !important;
  align-items: center !important;
  overflow: visible !important;
}

:deep(.ant-pagination-options .ant-select-selection-item) {
  line-height: 30px !important;
  font-size: 13px !important;
  white-space: nowrap !important;
  overflow: visible !important;
}

:deep(.ant-pagination-options .ant-select-arrow) {
  display: flex !important;
  align-items: center !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  right: 8px !important;
}

:deep(.ant-pagination-item),
:deep(.ant-pagination-prev),
:deep(.ant-pagination-next) {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 32px !important;
  height: 32px !important;
  line-height: 30px !important;
}

:deep(.ant-pagination-total-text) {
  display: inline-flex !important;
  align-items: center !important;
  height: 32px !important;
  font-size: 13px !important;
  color: #6c757d !important;
  white-space: nowrap !important;
}

/* Ensure the table wrapper doesn't clip the pagination */
:deep(.ant-table-wrapper) {
  overflow: visible !important;
}

:deep(.ant-table-pagination) {
  overflow: visible !important;
  margin: 12px 0 0 0 !important;
}

/* =============================================
   Stock Summary Bar
   ============================================= */
.stock-summary-bar {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.summary-pill {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.18s ease;
  background: #fff;
  user-select: none;
}
.summary-pill strong {
  font-size: 15px;
  font-weight: 700;
  margin-left: 4px;
}
.summary-pill.all { border-color: #dee2e6; color: #495057; }
.summary-pill.warning { border-color: #ffe0b2; color: #e65100; background: #fff8f2; }
.summary-pill.danger { border-color: #ffcdd2; color: #c62828; background: #fff5f5; }
.summary-pill.all.active { background: #f0f4ff; border-color: #3d5af1; color: #3d5af1; }
.summary-pill.warning.active { background: #fff3e0; border-color: #fb8c00; }
.summary-pill.danger.active { background: #ffebee; border-color: #e53935; }

.table-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.table-top-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.top-filter-select { min-width: 180px; }
.export-btn {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 5px 8px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  transition: all 0.15s;
}
.export-btn:hover { border-color: #3d5af1; background: #f0f4ff; }
.excel-icon { width: 22px; height: 22px; }

.item-name-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.item-name-link {
  font-size: 14px;
  font-weight: 600;
  color: #3d5af1;
  text-decoration: none;
}
.item-name-link:hover { text-decoration: underline; }
.item-code-tag {
  font-size: 11px;
  color: #9ea5b8;
  font-family: 'Courier New', monospace;
}

.price-value {
  font-weight: 600;
  color: #1a1a2e;
  font-size: 13px;
}

.stock-cell {
  display: flex;
  align-items: center;
  gap: 5px;
}
.stock-val { font-size: 13px; font-weight: 600; }
.stock-val.stock-ok { color: #2e7d32; }
.stock-val.stock-low { color: #e65100; }
.stock-val.stock-out { color: #c62828; }
.stock-unit { font-size: 11px; color: #9ea5b8; }
.stock-dot { width: 7px; height: 7px; border-radius: 50%; margin-left: 2px; }
.stock-dot.stock-ok { background: #43a047; }
.stock-dot.stock-low { background: #ef6c00; }
.stock-dot.stock-out { background: #e53935; }

.gst-badge {
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
}

.mono-text { font-family: 'Courier New', monospace; font-size: 12px; color: #555; }
.threshold-val { font-size: 13px; color: #6c757d; }

.action-cell { display: flex; align-items: center; gap: 8px; }
.action-btn img { width: 16px; height: 16px; }
.action-btn.view { opacity: 0.7; transition: opacity 0.15s; }
.action-btn.view:hover { opacity: 1; }

:deep(.ant-tooltip) { display: none !important; }

@media (max-width: 576px) {
  .stock-summary-bar { gap: 6px; }
  .summary-pill { padding: 6px 12px; font-size: 12px; }
  .table-top { flex-direction: column; align-items: flex-start; }
  .table-top-right { width: 100%; justify-content: space-between; }
  .top-filter-select { min-width: 140px; }
}

.expanded-wrap {
  background: #f8faff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 14px;
}
.detail-header {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 0.82rem;
  color: #6b7280;
}
.detail-table {
  width: 100%;
  border-collapse: collapse;
}
.detail-table th,
.detail-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.82rem;
  text-align: left;
  white-space: nowrap;
}
.detail-table th {
  color: #6b7280;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.72rem;
}
.expanded-loading {
  color: #6b7280;
  font-size: 0.82rem;
}
.bill-link {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}
.bill-link:hover {
  background: #dbeafe;
  color: #1d4ed8;
}
.remaining-full { color: #2e7d32; font-weight: 600; }
.remaining-partial { color: #e65100; font-weight: 600; }
.remaining-out { color: #c62828; font-weight: 600; }
</style>